import type { DailyItem } from "../lib/types";
import { useLang } from "../lib/useLang";
import { useCart } from "../state/CartContext";
import { useSheet } from "../state/SheetContext";
import { categoryIcon } from "../lib/categoryIcon";
import { Money } from "./Money";
import { QtyStepper } from "./QtyStepper";
import { SmartImage } from "./SmartImage";

const LOW_STOCK = 3;

/** A daily "fresh tray" card with a photo banner, used in the horizontal strip. */
export function DailyItemCard({ item }: { item: DailyItem }) {
  const { t, pick } = useLang();
  const { qtyOf, add, setQty } = useCart();
  const { open } = useSheet();
  const qty = qtyOf(item.id);
  const soldOut = item.soldOut || item.remainingQty <= 0;
  const low = !soldOut && item.remainingQty <= LOW_STOCK;

  return (
    <div
      className="group flex min-w-[76%] cursor-pointer snap-start flex-col overflow-hidden rounded-2xl bg-white shadow-soft ring-1 ring-black/5 transition hover:shadow-raised sm:min-w-[16rem]"
      onClick={() =>
        open({
          itemId: item.id,
          type: "daily",
          name: item.name,
          nameAr: item.nameAr,
          price: item.price,
          category: "daily",
          imageURL: item.imageURL,
          session: item.session,
          fastDelivery: item.fastDelivery,
          maxQty: item.remainingQty,
          soldOut,
        })
      }
    >
      {/* Photo banner */}
      <div className={`img-skeleton relative h-32 overflow-hidden ${soldOut ? "grayscale" : ""}`}>
        <SmartImage
          src={item.imageURL}
          alt={pick(item.name, item.nameAr)}
          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
          fallback={
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-brand/10 to-gold/15 text-5xl">
              {categoryIcon("daily")}
            </div>
          }
        />
        <div className="absolute inset-x-2 top-2 flex items-center justify-between">
          {item.fastDelivery ? (
            <span className="chip bg-white/90 text-brand shadow-sm">⚡ {t("common.fast")}</span>
          ) : (
            <span />
          )}
          {!soldOut &&
            (low ? (
              <span className="chip bg-gold/90 text-black shadow-sm">
                {t("common.lowStock", { count: item.remainingQty })}
              </span>
            ) : (
              <span className="chip bg-white/90 text-accent-dark shadow-sm">
                {t("common.remaining", { count: item.remainingQty })}
              </span>
            ))}
        </div>
        {soldOut && (
          <div className="absolute inset-0 grid place-items-center bg-black/45">
            <span className="rounded-full bg-white px-3 py-1 text-sm font-bold text-black/70">
              {t("common.soldOut")}
            </span>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col p-3.5">
        <div className="font-semibold leading-snug text-ink">
          {pick(item.name, item.nameAr)}
        </div>
        <div className="truncate text-sm text-black/45" dir="auto">
          {pick(item.nameAr, item.name)}
        </div>
        <div
          className="mt-2 flex items-center justify-between"
          onClick={(e) => e.stopPropagation()}
        >
          <Money value={item.price} className="price text-lg" />
          {soldOut ? null : qty > 0 ? (
            <QtyStepper qty={qty} max={item.remainingQty} onChange={(q) => setQty(item.id, q)} />
          ) : (
            <button
              className="add-fab"
              aria-label="add"
              onClick={() =>
                add({
                  itemId: item.id,
                  type: "daily",
                  name: item.name,
                  nameAr: item.nameAr,
                  price: item.price,
                  session: item.session,
                  fastDelivery: item.fastDelivery,
                  imageURL: item.imageURL,
                })
              }
            >
              +
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
